"use client";

import { getGlobalProperty } from "@faire/globals/deprecated/getGlobalProperty";
import { buildCurrencyFormatLocale } from "@faire/localization/core/buildCurrencyFormatLocale";
import { DEFAULT_LOCALE_KEY } from "@faire/localization/core/consts/DEFAULT_LOCALE";
import { createFormatMessage } from "@faire/localization/core/createFormatMessage";
import { buildLocale } from "@faire/localization/core/utils";
import { Language } from "@faire/types-localization-api/indigofair/languages/Language";
import { ILocaleKey } from "@faire/types-localization-api/indigofair/locale/ILocaleKey";
import { createIntl } from "@formatjs/intl/lib/src/create-intl";
import { IntlConfig, IntlShape } from "@formatjs/intl/lib/src/types";
import React, { createContext, useContext, useMemo } from "react";

type Store = {
  localeKey: ILocaleKey | undefined;
  currencyFormatLocale: string;
  shouldPassThrough: boolean;
  intl: IntlShape<string>;
  intlForCurrency: IntlShape<string>;
};

export type StoreProps = Partial<Omit<Store, "intl" | "intlForCurrency">>;

const getFormatMessage = (
  localeKey: ILocaleKey | undefined,
  formatMessage: IntlShape<string>["formatMessage"]
) => {
  if (
    localeKey?.language === Language.STRING_ID ||
    localeKey?.language === Language.GIBBERISH
  ) {
    return createFormatMessage(localeKey, formatMessage);
  }
  return formatMessage;
};

const buildIntl = ({
  localeKey,
  messages,
}: {
  localeKey: ILocaleKey | undefined;
  messages: IntlConfig<string>["messages"];
}): IntlShape<string> => {
  const locale = buildLocale(localeKey ?? DEFAULT_LOCALE_KEY);
  const intl = createIntl({
    locale: locale,
    defaultLocale: locale,
    messages: messages,
  });
  const formatMessage = getFormatMessage(localeKey, intl.formatMessage);
  return {
    ...intl,
    formatMessage,
  };
};

const DEFAULT_INTL = buildIntl({
  localeKey: DEFAULT_LOCALE_KEY,
  messages: {},
});

const DEFAULT_STORE: Store = {
  intl: buildIntl({
    localeKey: DEFAULT_LOCALE_KEY,
    messages: {},
  }),
  shouldPassThrough: false,
  localeKey: DEFAULT_LOCALE_KEY,
  currencyFormatLocale: DEFAULT_INTL.locale,
  intlForCurrency: DEFAULT_INTL,
};

const LocalizationContext = createContext<Store>(DEFAULT_STORE);

export const LocalizationProvider: React.FC<
  React.PropsWithChildren<StoreProps>
> = ({ children, localeKey, currencyFormatLocale, shouldPassThrough }) => {
  "use no memo";

  const knownLocaleKey =
    localeKey ?? getGlobalProperty<ILocaleKey>("localeKey");
  const resolvedLocaleKey = knownLocaleKey ?? DEFAULT_LOCALE_KEY;
  const resolvedShouldPassThrough = shouldPassThrough ?? false;
  const resolvedIntl = useMemo(
    () => buildIntl({ localeKey: resolvedLocaleKey, messages: {} }),
    [resolvedLocaleKey]
  );
  const resolvedCurrencyFormatLocale =
    buildCurrencyFormatLocale({
      localeKey: knownLocaleKey,
      currencyFormatLocale:
        currencyFormatLocale ||
        getGlobalProperty<string>("currencyFormatLocale"),
    }) ?? resolvedIntl.locale;
  const resolvedIntlForCurrency = useMemo(
    () =>
      resolvedCurrencyFormatLocale !== resolvedIntl.locale
        ? createIntl({
            locale: resolvedCurrencyFormatLocale,
            messages: {},
          })
        : resolvedIntl,
    [resolvedCurrencyFormatLocale, resolvedIntl]
  );

  const value = useMemo(
    (): Store => ({
      localeKey: resolvedLocaleKey,
      currencyFormatLocale: resolvedCurrencyFormatLocale,
      shouldPassThrough: resolvedShouldPassThrough,
      intl: resolvedIntl,
      intlForCurrency: resolvedIntlForCurrency,
    }),
    [
      resolvedLocaleKey,
      resolvedCurrencyFormatLocale,
      resolvedShouldPassThrough,
      resolvedIntl,
      resolvedIntlForCurrency,
    ]
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  return useContext(LocalizationContext);
};
